import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, ExternalLink, RotateCcw } from "lucide-react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import AdsAPIs from "../../../apiRoutes/Ads";
import CustomModal from "../../common/CustomModal";
import ButtonLoader from "../../Icons/ButtonLoader";
import LaunchStatusChip from "../LaunchStatusChip";
import { formatDisplayDateTime } from "../../../utils/helperFunctions";
import { copyTextToClipboard } from "../../../utils/clipboard";
import { displayIdOrOrphan } from "../../../utils/constants/ads";

const fmtRupees = (paise) => {
  if (paise == null) return "—";
  const n = Number(paise);
  if (!Number.isFinite(n) || n === 0) return "—";
  return `₹${Math.floor(n / 100).toLocaleString("en-IN")}`;
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
  </div>
);

const Field = ({ label, value }) => {
  const display = value === 0 ? 0 : (value || value === false ? value : "—");
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}</span>
      <div className="text-sm text-gray-800 break-all">{display === "" ? "—" : display}</div>
    </div>
  );
};

const AdLaunchDetailModal = ({ open, onClose, launchId, canRetry, onRetry }) => {
  const axiosPrivate = useAxiosPrivate();

  const { data: launch, isLoading } = useQuery({
    queryKey: ["ad_launch_detail", launchId],
    queryFn: () => AdsAPIs.getLaunch({ axiosPrivate, id: launchId }),
    enabled: open && !!launchId,
  });

  const settings = launch?.launch_settings || {};
  const snapshot = settings.campaign_snapshot || {};

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={launch ? `Launch #${launch.id} — Moderation #${launch.ad_moderate_id ?? "—"}` : "Launch details"}
      isButtonsRequired={false}
      className="!w-[95%] md:!w-[75%] lg:!w-[60%] !max-h-[90vh]"
    >
      {isLoading || !launch ? (
        <div className="flex items-center justify-center min-h-[200px] gap-2">
          <ButtonLoader color="#7f7ad5" />
          <span className="text-gray-600 font-medium">Loading…</span>
        </div>
      ) : (
        <div className="space-y-5 py-2">
          <Section title="Identifiers">
            <Field label="Launch ID" value={launch.id} />
            <Field label="Status" value={<LaunchStatusChip status={launch.status} />} />
            <Field label="Parent Ad ID" value={displayIdOrOrphan(launch.ad_id)} />
            <Field label="Moderation ID" value={launch.ad_moderate_id} />
          </Section>

          <Section title="Category & Track">
            <Field label="Category" value={launch.category} />
            <Field label="Track" value={launch.track} />
            <Field label="Track #" value={launch.track_number} />
            <Field label="Topic" value={launch.topic} />
          </Section>

          <Section title="Targets">
            <Field label="Meta Ad Account" value={launch.meta_ad_account_id} />
            <Field label="Meta Campaign" value={launch.meta_campaign_id} />
            <Field
              label="Meta Adset"
              value={launch.meta_adset_url ? (
                <a href={launch.meta_adset_url} target="_blank" rel="noreferrer" className="text-blue-700 inline-flex items-center gap-1 hover:underline">
                  {launch.meta_adset_id || "Open in Ads Manager"}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : launch.meta_adset_id}
            />
            <Field label="Meta Ad ID" value={launch.meta_ad_id} />
          </Section>

          <Section title="Campaign snapshot at launch">
            <Field label="Name" value={snapshot.name} />
            <Field label="Status" value={snapshot.status} />
            <Field label="Objective" value={snapshot.objective} />
            <Field label="Bid strategy" value={snapshot.bid_strategy} />
            <Field label="Daily budget" value={fmtRupees(snapshot.daily_budget)} />
            <Field label="Lifetime budget" value={fmtRupees(snapshot.lifetime_budget)} />
            <Field label="CBO" value={snapshot.has_cbo ? "Yes" : "No"} />
          </Section>

          <Section title="Adset settings">
            <Field label="Daily budget" value={fmtRupees(settings.adset_daily_budget)} />
            <Field label="Daily min spend" value={fmtRupees(settings.daily_min_spend_target)} />
            <Field label="Daily spend cap" value={fmtRupees(settings.daily_spend_cap)} />
            <Field label="Start time" value={settings.start_time ? formatDisplayDateTime(settings.start_time) : "—"} />
            <Field label="End time" value={settings.end_time ? formatDisplayDateTime(settings.end_time) : "—"} />
          </Section>

          <Section title="Schedule">
            <Field label="Scheduled at" value={launch.scheduled_at ? formatDisplayDateTime(launch.scheduled_at) : "—"} />
            <Field label="Cancelled at" value={launch.cancelled_at ? formatDisplayDateTime(launch.cancelled_at) : "—"} />
            <Field label="Cancelled by" value={launch.cancelled_by_name} />
          </Section>

          <Section title="Outcome">
            <Field label="Launched at" value={launch.launched_at ? formatDisplayDateTime(launch.launched_at) : "—"} />
            <Field label="Launched by" value={launch.launched_by_name} />
            <Field label="Meta Creative ID" value={launch.meta_creative_id} />
            <Field label="Retries" value={`${launch.retry_count ?? 0} / ${launch.max_retries ?? 0}`} />
          </Section>

          {launch.error_message && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-red-800">Error</span>
                <div className="flex items-center gap-2">
                  {canRetry && launch.id && onRetry && (
                    <button
                      type="button"
                      onClick={() => { onRetry(launch.id); onClose(); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white bg-primary hover:bg-primary/90"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Retry ({launch.retry_count})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => copyTextToClipboard(launch.error_message, "Error copied")}
                    className="p-1.5 rounded hover:bg-red-100"
                    title="Copy error"
                  >
                    <Copy className="w-3.5 h-3.5 text-red-700" />
                  </button>
                </div>
              </div>
              <pre className="text-xs text-red-900 whitespace-pre-wrap break-words font-mono max-h-[30vh] overflow-y-auto">
                {launch.error_message}
              </pre>
            </div>
          )}
        </div>
      )}
    </CustomModal>
  );
};

export default AdLaunchDetailModal;
